import type { Translations } from "./types";

const pt: Translations = {
  common: {
    brand_name: "Pirâmide de Desenvolvimento",
    child_development_assistance: "Assistência ao desenvolvimento infantil",
    child_name: "Nome da criança",
    child_gender: "Sexo da criança",
    child_age: "Idade da criança",
    tell_about_child:
      "Conte um pouco sobre seu filho para criarmos um teste individual e avaliar o seu desenvolvimento.",
    enter_name: "Digite o nome",
    continue: "Continuar",
    back: "Voltar",
    girl: "Menina",
    boy: "Menino",
    year: {
      one: "ano",
      few: "anos",
      many: "anos",
      short_unit: "a.",
    },
    month: {
      one: "mês",
      few: "meses",
      many: "meses",
      short_unit: "meses",
    },
    year_dative_one: "ano",
    year_dative_few: "anos",
    month_dative_one: "mês",
    month_dative_few: "meses",
    name_required: "Nome é obrigatório",
    name_too_long: "Nome deve ter no máximo 10 caracteres",
    name_invalid_chars: "Nome pode conter apenas letras e espaços",
    email_required: "Email é obrigatório",
    email_invalid: "Por favor, digite um email válido",
    email_too_long: "Email muito longo (máximo\u00A0100 caracteres)",
    email_invalid_chars: "Email contém caracteres inválidos",
    error_title: "Erro",
    try_again: "Tentar novamente",
    not_found: "Não encontrado",
    error_404_title: "404\u00A0Página não encontrada",
    error_404_description: "Esta página não pôde ser encontrada.",
    error_500_title: "500\u00A0Erro interno",
    error_500_description: "Algo deu errado.",
    start_over: "Começar o teste novamente",
    error: "Algo deu errado, tente novamente mais tarde",
    continue_questionnaire:
      "Você começou a fazer a pesquisa anteriormente, gostaria de continuar?",
    continue_button: "Continuar",
    later_button: "Mais tarde",
    categoryLabel: {
      FineMotor: "Habilidades motoras finas",
      GrossMotor: "Habilidades motoras grossas",
      VisualPerception: "Percepção visual",
      Speech: "Desenvolvimento da fala",
      HearingPerception: "Percepção auditiva",
      SocialDevelopment: "Desenvolvimento social",
    },
    submit: "Enviar",
    skip: "Pular",
    next: "Próximo",
    close: "Fechar",
    print: "Imprimir",
    share: "Compartilhar",
    male: "Masculino",
    female: "Feminino",
    start: "Iniciar",
    yes: "Sim",
    free: "Grátis",
    get_free_button: "Obter Grátis",
    pay_button: "Pagar",
    enter_button: "Inserir →",
    open_telegram_button: "Abrir o Telegram",
    progress_description:
      "As perguntas são divididas em categorias e você pode acompanhar o progresso do teste. À medida que avançamos, podemos adicionar perguntas esclarecedoras",
    question_instruction: "Leia a pergunta e responda",
  },
  seo: {
    default_title:
      "Descubra o nível de desenvolvimento do seu filho — grátis | Pirâmide de Desenvolvimento",
    default_description:
      "Identifique lacunas no desenvolvimento do seu filho, descubra os pontos fortes e receba recomendações personalizadas. $100 → Grátis. Iniciar diagnóstico.",
    fallback_description:
      "Avaliação abrangente do desenvolvimento infantil de 0 a 3\u00A0anos baseada em dados científicos modernos",
    organization_description:
      "Plataforma online para avaliação do desenvolvimento infantil de 0 a 3\u00A0anos",
    app_category: "Aplicativo Educacional",
    author_job_title: "Especialista em Desenvolvimento Infantil",
    author_name: "Darina Kopenkova",
    og_image_alt:
      "Pirâmide de Desenvolvimento - avaliação do desenvolvimento infantil 0–3\u00A0anos",
    language_code: "pt",
    locale: "pt_BR",
  },
  pages: {
    not_found: "404 - Página não encontrada",
  },
  landing: {
    hero_title:
      "<b>Identifique lacunas</b> no desenvolvimento do seu filho, <b>descubra os pontos fortes</b> e <b>receba recomendações personalizadas</b>.",
    free_for_limited_time: "Grátis por tempo limitado",
    comprehensive_diagnosis_subtitle: "em crianças de\u00A00–3\u00A0anos",
    comprehensive_diagnosis_based: "baseado em dados científicos atuais",
    price: "$100",
    comprehensive_diagnosis:
      "Diagnóstico abrangente de 0 a 3\u00A0anos nas principais áreas de desenvolvimento: fala, intelecto, habilidades motoras, emoções, sensorial, criatividade, adaptação.",
    step_1: "Passo\u00A01",
    step_2: "Passo\u00A02",
    step_3: "Passo\u00A03",
    faq_link: "FAQ",
    sample_report_link: "Relatório de Exemplo",
    social_subscribe_text:
      "Saiba mais sobre desenvolvimento infantil, brincadeiras para desenvolver habilidades e receba dicas úteis",
    questions_title:
      "O desenvolvimento infantil traz perguntas sem fim, dúvidas e a busca por respostas",
    sample_report: "Relatório de Exemplo",
    view_sample_report: "Ver Relatório de Exemplo",
    report_example_section_description:
      "Veja um relatório de exemplo para entender o nível de detalhamento e personalização da avaliação do seu filho",
    comprehensive_diagnosis_title: "Diagnóstico do desenvolvimento",
    diagnosis_words_intellectual: "intelectual",
    diagnosis_words_physical: "físico",
    diagnosis_words_social: "social",
    diagnosis_words_emotional: "emocional",
    diagnosis_words_speech: "da fala",
    diagnosis_words_sensorimotor: "sensorimotor",
    diagnosis_words_adaptive: "adaptativo",
    diagnosis_words_creative: "criativo",
    question_1: "Meu filho está se desenvolvendo no ritmo esperado?",
    question_2: "Já deveria estar falando?",
    question_3: "Senta sem apoio?",
    question_4: "Já engatinha?",
    question_5: "Fica de pé com apoio?",
    question_6: "Entende os pedidos?",
    question_7: "Come com colher?",
    question_8: "Já faz perguntas?",
    questions_description:
      "Chega de suposições e preocupações. Tenha uma visão completa do desenvolvimento do seu filho respondendo a perguntas simples",
    start_diagnosis: "Iniciar diagnóstico",
    step_1_text: "Informe o sexo e a idade da criança",
    step_2_text:
      'Responda "Sim" ou "Não" a um conjunto de perguntas personalizadas',
    step_3_text: "Receba um relatório personalizado",
    result_title:
      "Você receberá a avaliação do nível geral de desenvolvimento, uma análise detalhada das principais áreas, gráficos claros e recomendações personalizadas.",
    benefits_title:
      "Você terá tranquilidade, confiança e um plano de ação ao saber:",
    benefit_1:
      "A que idade o desenvolvimento do seu filho se assemelha em cada área-chave",
    benefit_2:
      "Quão equilibrados estão a linguagem, a motricidade, o pensamento e as emoções",
    benefit_3:
      "Em que a criança está à frente dos pares e onde é preciso reforçar o apoio",
    benefit_4:
      "Quais são os próximos passos — com recomendações claras e fáceis de aplicar",
    faq_title: "Respondemos às principais perguntas dos pais:",
    faq_1_q: "A partir de que idade pode ser feito o diagnóstico?",
    faq_1_a:
      "Adequado para crianças do nascimento aos 3\u00A0anos. As perguntas são adaptadas para cada idade e nível de desenvolvimento.",
    faq_2_q: "Quão preciso e confiável é este diagnóstico?",
    faq_2_a:
      "As perguntas e estrutura são desenvolvidas com base em abordagens científicas para o desenvolvimento precoce e adaptadas por especialistas. Você obtém um guia de qualidade, não conselhos aleatórios da internet.",
    faq_3_q: "Este é um teste online? Preciso baixar algo?",
    faq_3_a:
      "Tudo é completamente online. Sem instalações ou complicações — você apenas responde perguntas em um formato conveniente.",
    faq_4_q: "Quanto tempo leva para completar o diagnóstico?",
    faq_4_a:
      "De 10 a 20\u00A0minutos — dependendo da idade e características da criança. As perguntas são simples e claras.",
    faq_5_q: "O que estará no relatório?",
    faq_5_a:
      "Avaliação detalhada do desenvolvimento em todas as áreas-chave, gráficos visuais e recomendações personalizadas adaptadas à idade.",
    faq_6_q: "Posso mostrar o relatório a um especialista?",
    faq_6_a:
      "Sim, o relatório é conveniente para salvar, imprimir ou encaminhar. Pode ser discutido com um pediatra, neurologista, psicólogo ou educador.",
    faq_7_q: "Isso é seguro? O que vocês fazem com meus dados?",
    faq_7_a:
      "Todos os dados são protegidos. Não compartilhamos informações com terceiros e as usamos apenas para formar resultados e melhorar o serviço.",
    faq_8_q:
      'Meu filho não tem desenvolvimento "típico" — devo fazer o diagnóstico?',
    faq_8_a:
      "Sim. O diagnóstico ajudará a entender o nível atual de desenvolvimento e dará recomendações, independentemente do ritmo e características individuais.",
    faq_9_q: "E se eu responder algo errado?",
    faq_9_a:
      "Sem problemas — as perguntas são simples e intuitivas. E o relatório não é um diagnóstico, mas um guia para compreensão e desenvolvimento.",
    sharing_1: "O relatório é fácil de salvar, compartilhar ou imprimir.",
    sharing_2:
      "Compartilhe as conquistas com familiares ou apresente a um especialista.",
    personal_plan_title:
      "Quer receber um plano de desenvolvimento personalizado para o seu filho com base na análise?",
    app_announcement_title:
      "Estamos lançando um aplicativo com um programa de desenvolvimento simples e adaptável — feito para o seu filho.",
    app_announcement_description:
      "Basta dedicar 15–30\u00A0minutos por dia a brincadeiras simples, e seu filho vai se desenvolver mais rápido do que os pares — em linguagem, movimento, pensamento e muito mais.",
    it_works_title:
      "Isso funciona quando as habilidades são desenvolvidas de forma sequencial e no momento certo — quando o cérebro e o corpo estão mais prontos para aprender.",
    it_works_description:
      "Chega de gastar tempo e dinheiro com atividades duvidosas ou se perder em um mar de informações. Não é preciso adivinhar como engajar a criança nem se você está fazendo tudo certo. Apenas passem tempo juntos — com propósito, interesse e confiança. Os jogos não vão mais ficar parados nas prateleiras.",
    it_works_alt: "Bebê na mão",
    copyright: "Todos os direitos reservados.",
    privacy_policy: "Política de Privacidade",
    terms_of_use: "Termos de Uso",
    support: "Suporte",
    mobile_menu: {
      faq: "Perguntas Frequentes",
    },
  },
  questionnaire: {
    error: "Algo deu errado, tente novamente mais tarde ou comece do início",
    question_progress: "{{current}} de {{total}}",
    answer_no: "Não",
    answer_not_sure: "Não tem certeza?",
    answer_pending:
      "Por favor, aguarde, estamos processando sua resposta anterior",
    answer_error: "Algo deu errado, tente novamente",
    completedTitle: "Pronto!",
    completedSubtitle1: "Por favor, insira seu email.",
    completedSubtitle2:
      "Você receberá imediatamente um link pelo qual poderá ver os resultados do diagnóstico a qualquer momento",
    welcome_title: "🚀 Desbloqueie o potencial do seu filho",
    welcome_description:
      "📈 Faça uma avaliação de habilidades e obtenha um programa de desenvolvimento personalizado",
    how_it_works: "Como funciona:",
    step_1: "1.\u00A0Responda perguntas simples.",
    step_2:
      "2.\u00A0Obtenha imediatamente um relatório e cronograma de atividades personalizado.",
    step_3: "3.\u00A0Siga instruções simples para cada atividade.",
    time_section: "Tempo:",
    time_questionnaire: "Preencher o questionário leva 5–10\u00A0minutos.",
    time_activities: "Passe 1–2\u00A0horas por dia com seu filho.",
    result_section: "Resultado:",
    result_report: "Relatório detalhado.",
    result_schedule: "Cronograma individual.",
    result_tips: "Dicas úteis.",
    result_guarantee: "Resultado garantido.",
    learn_more: "Saiba mais",
    get_acquainted: "Vamos nos conhecer!",
    get_acquainted_description:
      "Responda algumas perguntas para criarmos um teste individual para diagnóstico do desenvolvimento do seu bebê",
    specify_gender: "Informe o sexo da criança",
    specify_age: "Informe a idade da criança",
    months: "meses",
    email: {
      placeholder: "Seu email",
      submit: "Obter resultados",
      verification1:
        "Enviamos um email com um link para os resultados do teste para seu endereço",
      verification2:
        "Se você não recebeu o email, verifique sua pasta de spam ou entre em contato conosco em",
      resend_email: "Reenviar email de verificação",
      rate_limit:
        "Por favor, aguarde {{seconds}}\u00A0segundos antes de tentar novamente",
      email_sent_again: "Email de verificação reenviado",
      invalid: "Por favor, insira um email válido",
      delivery_time: "Os emails geralmente chegam em\u00A02‑5\u00A0minutos",
      check_spam: "💡 Verifique sua pasta de spam",
      change_or_resend: "Alterar email ou reenviar",
    },
  },
  results: {
    mdHeaderTitle: "Resultados do teste",
    age_gauge_description:
      "Idade correspondente ao desenvolvimento do seu filho",
    read_more: "Ler mais",
    app_announcement_success:
      "Ótimo! Você está na lista dos primeiros usuários que terão acesso ao programa. Definitivamente notificaremos você quando estiver pronto.",
    app_announcement_description:
      "Agora faça o diagnóstico para obter um plano baseado nos resultados quando o app estiver pronto",
    start_test: "Iniciar teste",
    want_personal_plan:
      "Gostaria de obter um plano de desenvolvimento personalizado para seu filho com base nesta análise?",
    want_personal_plan_button: "Quero um plano personalizado",
    personal_plan_button: "Plano personalizado",
    start_diagnostic_button: "Fazer diagnóstico",
    want_app_form: {
      title: "Obter um plano de desenvolvimento personalizado",
      description:
        "Deixe seu email e você será um dos primeiros a saber quando o app com um plano de desenvolvimento personalizado for lançado",
      email_placeholder: "Seu email",
      comment_placeholder: "Qualquer comentário (opcional)",
      submit: "Obter Plano",
    },
  },
  feedback: {
    dialog_title: "Como foi sua experiência?",
    dialog_subtitle: "Seu feedback nos ajuda a melhorar",
    optional_comment: "Opcional: Conte-nos mais sobre sua experiência…",
  },
  test: {
    consent_text:
      "Consinto com o tratamento dos dados pessoais do meu filho (nome, sexo, idade, resultados do diagnóstico) e dos meus dados pessoais (e-mail) para a realização do diagnóstico de desenvolvimento, a formulação de recomendações personalizadas, o envio dos resultados por e-mail e o recebimento de informações sobre novas funcionalidades do serviço, de acordo com a <a href='/privacy-policy'>Política de Privacidade</a> e os <a href='/terms-of-use'>Termos de Uso</a>.",
    consent_required:
      "Por favor, aceite o termo de consentimento para continuar",
    promo_invalid: "Código promocional inválido ou já usado",
  },
  promo: {
    generate_button: "Gerar",
    copy_button: "Copiar",
    copied_new: "Novo código promocional copiado",
    generated: "Código promocional criado",
  },
  payment: {
    header_title: "Descubra os pontos fortes do seu filho",
    header_description:
      "Avaliação de 10\u00A0minutos — obtenha um plano de desenvolvimento personalizado",
    card_title_telegram: "Telegram",
    helper_text:
      "Inscreva-se no Telegram → Comente → Receba o link por mensagem privada",
    caption_text:
      "O link com o código promocional chegará por mensagem privada",
    icon_comment: "Comentário",
    icon_code_dm: "Código por mensagem privada",
    dialog_title: "Como obter acesso gratuito",
    dialog_step_1: "Abra esta publicação no Telegram",
    dialog_step_2: "Deixe um comentário",
    dialog_step_3: "Inscreva-se no canal",
    dialog_step_4:
      "Você receberá um link para o diagnóstico gratuito por mensagem privada",
    payment_title: "Pagamento",
    payment_price: "$100",
    payment_caption: "Acesso instantâneo. O recibo será enviado por e-mail.",
    or_divider: "OU",
    promo_code_text: "Tenho um código promocional",
    privacy_text:
      "🔒 Não publicamos seus dados • O acesso permanece disponível para sempre",
  },
  report: {
    list: {
      need_fast_development:
        "{{count}} precisam de desenvolvimento mais rápido",
      mastered_on_time: "{{count}} dominadas no prazo",
      mastered_early: "{{count}} habilidades dominadas antes dos pares",
    },
    stability_stable:
      "Mantenha assim! Desenvolvimento estável apropriado para a idade.",
    stability_ahead:
      "Excelente! A criança está significativamente adiantada em relação às normas de idade.",
    stability_unstable:
      "Desenvolvimento instável: há grupos adiantados e atrasados — considere uma abordagem sistêmica.",
    stability_behind:
      "A criança está um pouco atrasada em relação às normas — considere introduzir atividades para habilidades em atraso.",
    stability_too_behind:
      "A criança está significativamente atrasada — considere consultar um especialista e introduzir atividades direcionadas.",
    stability_too_behind_unstable:
      "Atraso significativo nas habilidades etárias-chave combinado com áreas adiantadas. Considere consultar um especialista e uma abordagem sistêmica ao desenvolvimento.",
    overview: {
      title: "Desenvolvimento por categorias",
    },
    legend: {
      mastered: "Dominado",
      early: "Dominado antes",
    },
    labels: {
      advanced_mastered: "Dominado com antecedência",
      matching_mastered: "Dominado no prazo",
      missing: "Não dominado",
      missing_overdue: "Não dominado, prazo ultrapassado",
      on_time: "No prazo",
      advanced: "Adiantado",
      missed: "Perdido",
    },
    stats: {
      should_be_mastered_label: "Deve ser dominado até {{age}}",
    },
    words: {
      skill_one: "habilidade",
      skill_few: "habilidades",
      skill_many: "habilidades",
    },
    progress: {
      you_mastered: "Você dominou",
    },
    skills: {
      of_them: "Dentre eles",
      to_master: "A dominar ({{count}})",
      mastered_on_time: "Dominado no prazo ({{count}})",
      mastered_in_advance: "Dominado com antecedência ({{count}})",
    },
    conclusions: {
      excellent_title: "Desenvolvimento excelente",
      excellent_subtitle: "Adiantado em relação à idade:",
      excellent_text:
        "isso significa que nessas categorias as habilidades se desenvolvem mais rápido que o esperado.",
      well_title: "Bom desenvolvimento",
      well_subtitle: "De acordo com a idade:",
      well_text:
        "as habilidades estão se desenvolvendo conforme as normas de idade.",
      steady_title: "Desenvolvimento estável",
      steady_subtitle: "Requer manutenção do ritmo:",
      steady_text:
        "as habilidades se desenvolvem de forma estável; é importante manter o ritmo atual.",
      uneven_title: "Desenvolvimento irregular",
      uneven_subtitle: "Requer atenção:",
      uneven_text: "há flutuações observadas, exigindo atenção adicional.",
      critical_title: "Atraso crítico",
      critical_subtitle: "Requer atenção urgente:",
      critical_text:
        "as habilidades estão significativamente atrasadas em relação ao normal e requerem ação imediata.",
    },
    cta: {
      title:
        "✨ Gostaria de obter um plano de desenvolvimento personalizado com base na análise? ✨",
      p1: "🚀 Estamos lançando um app com um programa de desenvolvimento simples e claro, ajustado continuamente para seu filho.",
      p2: "⏰ Com 15–30\u00A0minutos por dia, ajude seu filho a alcançar e superar as normas de idade nas principais áreas — intelecto, motricidade, fala.",
      p3: "💡 Funciona quando as habilidades são desenvolvidas de forma consistente e no momento certo, quando cérebro e corpo aprendem com mais eficácia.",
      p4: "Sem mais aulas em grupo duvidosas ou informações desconexas — tenha um plano claro e ações concretas!",
      p5: "🎯 Passe tempo com seu filho de forma eficaz. As caixas de jogos não acumularão poeira!",
    },
    pie: {
      main_text:
        "<0>Seu filho</0> <1>já dominou <2>{{totalMastered}}</2></1><3><4>, dos quais <5>{{advancedMastered}}</5> (<6>{{advancedPercent}}%</6>) foram dominados antes dos pares</4></3>",
      missing_text:
        "Infelizmente, <0>ainda não foram dominadas <1>{{missing}}</1> (<2>{{missingPercent}}%</2>)</0> de <3>{{totalExpected}}</3>. No entanto, já há uma boa base para recuperar rapidamente.",
    },
    ai_report: {
      print_title: "Relatório geral de habilidades de desenvolvimento",
      print_error:
        "A impressão não está disponível neste navegador. Tente usar um navegador diferente ou copie o texto manualmente.",
      loading_title:
        "Mostraremos uma conclusão geral e recomendações personalizadas aqui em alguns minutos",
      error_title:
        "O relatório geral de desenvolvimento não pode ser carregado agora",
      error_description:
        "Tente atualizar a página em 30\u00A0minutos ou tente novamente agora",
      error_retry: "Tentar novamente",
      not_ready_title:
        "O relatório geral de desenvolvimento ainda não está pronto",
      not_ready_description: "Tente atualizar a página em alguns minutos",
      not_ready_refresh: "Atualizar",
      mastered_on_time: "dominado no tempo",
      percent_mastered_on_time: "percentual dominado no tempo",
      missing_required:
        "não dominados, mas que deveriam ter sido dominados nesta idade",
      percent_missing_on_time: "percentual não dominado no tempo",
      mastered_early: "dominados mesmo antes da idade normativa",
      percent_mastered_early: "percentual dominado antes da idade normativa",
      next_to_master: "próximos a dominar",
    },
    share: {
      title: "Relatório de desenvolvimento de {{name}}",
      text: "Confira os resultados da avaliação de desenvolvimento de {{name}}",
    },
    categories_stability: {
      behind_title: "Há categorias atrasadas",
      ahead_title: "Desenvolvimento avançado",
      balanced_title: "Desenvolvimento equilibrado",
      balanced_description:
        "Todas as categorias estão suficientemente desenvolvidas",
      behind_lags: "atrasadas em relação às normas de idade",
      behind_lag: "atrasada em relação às normas de idade",
      behind_with_advanced: "em combinação com habilidades avançadas",
      unstable_developed: "desenvolvidas de forma desigual",
      unstable_develop: "desenvolvida de forma desigual",
      stable_sufficient: "suficientemente desenvolvida",
      stable_sufficient_plural: "suficientemente desenvolvidas",
      ahead_ahead: "adiantada em relação às normas de idade",
      ahead_aheads: "adiantadas em relação às normas de idade",
      category: "categoria",
      categories: "categorias",
      categories_many: "categorias",
      though: "embora",
      and: "e",
      and_yet: "e ainda",
    },
  },
  tutorial: {
    if_child_can: "se a criança pode realizar a ação especificada.",
    if_child_cannot:
      "Se a criança não possui ou possui a habilidade apenas parcialmente - clique",
    hint_description:
      'Se a pergunta não estiver muito clara ou você tiver dúvidas, clique em "não tenho certeza" para ler a dica com critérios de resposta para esta pergunta.',
    auto_save: "Cada resposta",
    auto_save_highlight: "é automaticamente salva no navegador",
    can_close_and_return:
      "Você pode fechar a aba a qualquer momento e retornar para continuar o teste mais tarde, começando da pergunta onde parou.",
  },
  landing_assets: {
    questionnaire: {
      question_1_name: 'Pode seguir um pedido: "Me dê dois..."',
      question_1_hint:
        'Seu filho consegue dar uma quantidade específica de objetos quando solicitado (por exemplo, "Me dê dois blocos", "Me dê três bolas")? Seu filho entende os números "dois", "três" e consegue seguir esses pedidos?',
      question_2_name:
        "Seu filho entende os nomes de muitos objetos familiares, pessoas e algumas ações?",
      question_2_hint:
        "Seu filho consegue apontar ou dar um objeto familiar quando solicitado (como 'me dê a bola' ou 'onde está o ursinho?'), apontar para uma pessoa familiar ou entender nomes de ações simples (como 'beije a mamãe' ou 'acene tchau')?",
      question_11_name: "Pode expressar pensamentos em palavras curtas",
      question_12_name: "Uma criança consegue desabotoar um botão",
      question_13_name:
        "Pode interagir com pessoas diferentes que se tornam familiares",
    },
    report: {
      child_name: "Kate",
    },
  },
};

export default pt;
